import { useState } from 'react';
import { ChevronDown, ChevronRight, FileText, Mail, Database, Search, Plus, GitFork } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import type { TemplateTreeNode, TemplateType } from '../../types/template.types';

interface TemplateTreeProps {
  selectedId: string | null;
  baseTemplates: TemplateTreeNode[];
  tenantTemplates: TemplateTreeNode[];
  tenantLabel?: string;
  onSelect: (template: TemplateTreeNode) => void;
  onNewTemplate: () => void;
  onNewBaseTemplate: () => void;
  onForkTemplate: () => void;
  isLoading?: boolean;
}

function TypeIcon({ type, className = '' }: { type: TemplateType; className?: string }) {
  if (type === 'pdf') return <FileText className={`h-3.5 w-3.5 text-pdf ${className}`} />;
  if (type === 'ledes') return <Database className={`h-3.5 w-3.5 text-ledes ${className}`} />;
  return <Mail className={`h-3.5 w-3.5 text-email ${className}`} />;
}

function TreeGroup({
  label,
  templates: items,
  selectedId,
  onSelect,
  defaultOpen = false,
  onAction,
}: {
  label: string;
  templates: TemplateTreeNode[];
  selectedId: string | null;
  onSelect: (t: TemplateTreeNode) => void;
  defaultOpen?: boolean;
  onAction?: () => void;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const emailTemplates = items.filter(t => t.type === 'email');
  const pdfTemplates = items.filter(t => t.type === 'pdf');
  const ledesTemplates = items.filter(t => t.type === 'ledes');

  return (
    <div>
      <div className="flex w-full items-center">
        <button
          onClick={() => setOpen(!open)}
          className="flex flex-1 items-center gap-1.5 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
        >
          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          {label}
          <span className="ml-auto text-[10px] font-normal">{items.length}</span>
        </button>
        {onAction && (
          <button
            onClick={e => { e.stopPropagation(); onAction(); }}
            className="mr-2 p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            title={`New ${label.replace(' Templates', '').toLowerCase()} template`}
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {open && (
        <div className="ml-2">
          {emailTemplates.length > 0 && (
            <SubGroup label="Email" items={emailTemplates} selectedId={selectedId} onSelect={onSelect} />
          )}
          {pdfTemplates.length > 0 && (
            <SubGroup label="PDF" items={pdfTemplates} selectedId={selectedId} onSelect={onSelect} />
          )}
          {ledesTemplates.length > 0 && (
            <SubGroup label="LEDES" items={ledesTemplates} selectedId={selectedId} onSelect={onSelect} />
          )}
        </div>
      )}
    </div>
  );
}

function SubGroup({
  label,
  items,
  selectedId,
  onSelect,
}: {
  label: string;
  items: TemplateTreeNode[];
  selectedId: string | null;
  onSelect: (t: TemplateTreeNode) => void;
}) {
  const [open, setOpen] = useState(true);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-1.5 px-3 py-1 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        {open ? <ChevronDown className="h-2.5 w-2.5" /> : <ChevronRight className="h-2.5 w-2.5" />}
        {label}
      </button>
      {open && (
        <div className="ml-1">
          {items.map(t => (
            <button
              key={t.id}
              onClick={() => onSelect(t)}
              className={`flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors ${
                selectedId === t.id
                  ? 'bg-accent text-accent-foreground font-medium'
                  : 'text-foreground/80 hover:bg-accent/50'
              }`}
            >
              <TypeIcon type={t.type} />
              <span className="truncate flex-1 text-left">{t.name}</span>
              {t.activeVersion && (
                <span className="h-1.5 w-1.5 rounded-full bg-active flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TemplateTree({
  selectedId,
  baseTemplates,
  tenantTemplates,
  tenantLabel,
  onSelect,
  onNewTemplate,
  onNewBaseTemplate,
  onForkTemplate,
  isLoading,
}: TemplateTreeProps) {
  const [search, setSearch] = useState('');

  const filtered = (items: TemplateTreeNode[]) =>
    search
      ? items.filter(t => t.name.toLowerCase().includes(search.toLowerCase()) || t.path.toLowerCase().includes(search.toLowerCase()))
      : items;

  return (
    <div className="flex h-full flex-col bg-studio-surface border-r border-border">
      {/* Search */}
      <div className="p-3 border-b border-border">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search templates..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="h-8 pl-8 text-sm bg-muted/50 border-0 focus-visible:ring-1"
          />
        </div>
      </div>

      {/* Tree */}
      <div className="flex-1 overflow-y-auto py-2 studio-scrollbar" data-tour="template-tree">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <span className="text-xs text-muted-foreground">Loading templates...</span>
          </div>
        ) : (
          <>
            <TreeGroup
              label="Base Templates"
              templates={filtered(baseTemplates)}
              selectedId={selectedId}
              onSelect={onSelect}
              onAction={onNewBaseTemplate}
              defaultOpen
            />
            {tenantTemplates.length > 0 && (
              <TreeGroup
                label={tenantLabel ?? 'Tenant Templates'}
                templates={filtered(tenantTemplates)}
                selectedId={selectedId}
                onSelect={onSelect}
                defaultOpen
              />
            )}
          </>
        )}
      </div>

      {/* Actions */}
      <div className="p-3 border-t border-border space-y-2">
        <Button variant="outline" size="sm" className="w-full justify-start gap-2 h-8 text-xs" onClick={onNewTemplate}>
          <Plus className="h-3.5 w-3.5" />
          New Template
        </Button>
        <Button variant="outline" size="sm" className="w-full justify-start gap-2 h-8 text-xs" onClick={onForkTemplate}>
          <GitFork className="h-3.5 w-3.5" />
          Fork Base to Tenant
        </Button>
      </div>
    </div>
  );
}
